/**
 * sessionStorage bridge for the invitation → sign-in → accept flow.
 *
 * The flow is:
 *
 *   1. Invitee clicks `/invite/{token}` link from email, lands on
 *      AcceptInvitationPage (public, no auth required).
 *   2. AcceptInvitationPage fetches the public inspect endpoint and
 *      shows the preview.
 *   3. If the invitee isn't signed in, the page offers a "Sign in
 *      with Google to accept" button. That button:
 *        - calls `stashPendingInviteToken(token)`
 *        - navigates the browser to `/api/auth/google/login`
 *   4. Google OAuth round-trip returns the user to the app root,
 *      which routes to `/dashboard` via RequireAuth.
 *   5. RequireFamily fires for an active user with no family,
 *      checks sessionStorage via `consumePendingInviteToken()`,
 *      and — if a token is found — redirects to `/invite/{token}`
 *      instead of `/family/create`. The stored token is cleared
 *      on read so the redirect only happens once.
 *   6. AcceptInvitationPage reopens, sees the authenticated user,
 *      and shows the final "Accept" button (or the email-mismatch
 *      error if the wrong account was used).
 *
 * sessionStorage is the right scope here — not localStorage —
 * because we want the token cleared when the user closes the tab
 * (it's a short-lived, one-session intent).
 */

export const PENDING_INVITE_TOKEN_KEY = "mic_pending_invite_token";

function safeStorage(): Storage | null {
  // sessionStorage can be unavailable in some embedded browser contexts
  // (private mode on some Safari versions). Fail soft rather than crash.
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function stashPendingInviteToken(token: string): void {
  const storage = safeStorage();
  if (storage === null) return;
  try {
    storage.setItem(PENDING_INVITE_TOKEN_KEY, token);
  } catch {
    // quota exceeded or blocked — non-fatal
  }
}

/**
 * Read and remove the pending invite token in one step.
 *
 * Returns the token string if one was stored, or null if not.
 * The "consume" semantics (read + remove) ensure the redirect
 * happens at most once — after the invitee accepts (or fails),
 * a later /dashboard visit won't re-bounce them to /invite.
 */
export function consumePendingInviteToken(): string | null {
  const storage = safeStorage();
  if (storage === null) return null;
  try {
    const value = storage.getItem(PENDING_INVITE_TOKEN_KEY);
    if (value !== null) {
      storage.removeItem(PENDING_INVITE_TOKEN_KEY);
    }
    return value;
  } catch {
    return null;
  }
}

export function clearPendingInviteToken(): void {
  const storage = safeStorage();
  if (storage === null) return;
  try {
    storage.removeItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    // ignore
  }
}
